/**
 * Global power menu — opens with ⌘K / Ctrl+K from anywhere.
 *
 * Actions:
 *   • New job → /jobs/new
 *   • Upload resume → /candidates → opens the dropzone (we navigate + dispatch a window event the page can listen to)
 *   • Add reminder → dispatch a `pw-open-reminder` event that CalendarPage listens to,
 *     or navigate to /calendar?openNote=1 if not already there.
 *   • Schedule interview → /calendar?openSlot=1 (creates an empty slot on today and opens the dialog)
 *   • Quick search candidates / jobs — live results, click → navigate to detail page.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    Command, Briefcase, UserPlus, Bell, Calendar as CalIcon, Search, ChevronRight,
    Sparkles, ListPlus, Users as UsersIcon, FileUp, Palette, Mail, Tag, ShieldCheck,
    LayoutTemplate, Inbox, CreditCard,
} from "lucide-react";

// Recruiter / employer actions (the default set)
const RECRUITER_ACTIONS = [
    { id: "new-job", label: "Create job", icon: Briefcase, kbd: "J", group: "Create", to: "/jobs/new" },
    { id: "upload-resume", label: "Upload resume", icon: FileUp, kbd: "U", group: "Create", to: "/candidates?upload=1" },
    { id: "schedule-interview", label: "Schedule interview", icon: CalIcon, kbd: "I", group: "Create", to: "/calendar?openSlot=1" },
    { id: "bulk-slots", label: "Bulk create interview slots", icon: Sparkles, kbd: "B", group: "Create", to: "/calendar?bulkSlots=1" },
    { id: "add-reminder", label: "Add reminder / note", icon: Bell, kbd: "R", group: "Create", to: "/calendar?openNote=1" },
    { id: "go-dashboard", label: "Go to dashboard", icon: Command, group: "Navigate", to: "/" },
    { id: "go-candidates", label: "All candidates", icon: UsersIcon, group: "Navigate", to: "/candidates" },
    { id: "go-jobs", label: "All jobs", icon: Briefcase, group: "Navigate", to: "/jobs" },
    { id: "go-calendar", label: "Calendar", icon: CalIcon, group: "Navigate", to: "/calendar" },
    { id: "go-lists", label: "Hiring lists", icon: ListPlus, group: "Navigate", to: "/lists" },
    { id: "go-team", label: "Team leaderboard", icon: UsersIcon, group: "Navigate", to: "/team" },
];

// Admin-specific actions
const ADMIN_ACTIONS = [
    { id: "admin-dashboard", label: "Go to admin overview", icon: ShieldCheck, kbd: "A", group: "Navigate", to: "/admin" },
    { id: "edit-landing", label: "Edit landing page", icon: LayoutTemplate, kbd: "L", group: "Manage platform", to: "/admin/landing-content" },
    { id: "branding", label: "Platform branding", icon: Palette, kbd: "B", group: "Manage platform", to: "/admin#branding" },
    { id: "email-templates", label: "Email templates", icon: Mail, kbd: "E", group: "Manage platform", to: "/admin#email-templates" },
    { id: "discount-codes", label: "Discount codes", icon: Tag, kbd: "D", group: "Manage platform", to: "/admin#discount-codes" },
    { id: "revenue", label: "Revenue / MRR", icon: CreditCard, kbd: "R", group: "Manage platform", to: "/admin#revenue" },
    { id: "kyc", label: "KYC requests", icon: ShieldCheck, group: "Review", to: "/admin#kyc" },
    { id: "enquiries", label: "Hiring enquiries inbox", icon: Inbox, kbd: "I", group: "Review", to: "/admin#enquiries" },
];

export default function CommandMenu() {
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [cands, setCands] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [hi, setHi] = useState(0);
    const inputRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const tref = useRef(null);
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const ACTION_ITEMS = isAdmin ? ADMIN_ACTIONS : RECRUITER_ACTIONS;

    // Open on ⌘K / Ctrl+K
    useEffect(() => {
        const onKey = (e) => {
            const isMac = navigator.platform.toUpperCase().includes("MAC");
            const ctrl = isMac ? e.metaKey : e.ctrlKey;
            if (ctrl && (e.key === "k" || e.key === "K")) {
                e.preventDefault();
                setOpen((o) => !o);
            }
            if (e.key === "Escape") setOpen(false);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    // Allow other parts of the app to open the menu programmatically.
    useEffect(() => {
        const fn = () => setOpen(true);
        window.addEventListener("pw-open-power-menu", fn);
        return () => window.removeEventListener("pw-open-power-menu", fn);
    }, []);

    // Reset & focus on open
    useEffect(() => {
        if (open) {
            setQ(""); setCands([]); setJobs([]); setHi(0);
            setTimeout(() => inputRef.current?.focus(), 30);
        }
    }, [open]);

    // Debounced quick search (skip for admin — they don't own candidates/jobs)
    useEffect(() => {
        if (!open || isAdmin) return;
        if (tref.current) clearTimeout(tref.current);
        if (!q.trim()) { setCands([]); setJobs([]); return; }
        tref.current = setTimeout(() => {
            Promise.all([
                api.get("/calendar/quick-candidates", { params: { q, limit: 6 } }).then((r) => r.data.items || []).catch(() => []),
                api.get("/calendar/quick-jobs", { params: { q, limit: 6 } }).then((r) => r.data.items || []).catch(() => []),
            ]).then(([cs, js]) => { setCands(cs); setJobs(js); });
        }, 180);
        return () => tref.current && clearTimeout(tref.current);
    }, [q, open, isAdmin]);

    const filteredActions = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return ACTION_ITEMS;
        return ACTION_ITEMS.filter((a) => a.label.toLowerCase().includes(term) || (a.group || "").toLowerCase().includes(term));
    }, [q, ACTION_ITEMS]);

    const flat = useMemo(() => {
        // Build a flat keyboard-navigable list:  actions, then cands, then jobs
        return [
            ...filteredActions.map((a) => ({ kind: "action", ...a })),
            ...cands.map((c) => ({ kind: "candidate", id: c.id, label: c.name || c.email, sub: c.current_title || c.email, item: c })),
            ...jobs.map((j) => ({ kind: "job", id: j.id, label: j.title, sub: j.status || "", item: j })),
        ];
    }, [filteredActions, cands, jobs]);

    useEffect(() => { setHi(0); }, [q]);

    const handleAction = (a) => {
        setOpen(false);
        if (a.kind === "candidate") return navigate(`/candidates/${a.id}`);
        if (a.kind === "job") return navigate(`/jobs/${a.id}`);
        if (a.to) {
            // If we're already on calendar and the action is a calendar shortcut, dispatch instead of navigate
            if (a.to.startsWith("/calendar") && location.pathname.startsWith("/calendar")) {
                const evt = a.to.includes("openSlot") ? "pw-cal-open-slot"
                    : a.to.includes("bulkSlots") ? "pw-cal-open-bulk"
                        : a.to.includes("openNote") ? "pw-cal-open-note" : null;
                if (evt) {
                    window.dispatchEvent(new CustomEvent(evt));
                    return;
                }
            }
            navigate(a.to);
        }
    };

    const onKey = (e) => {
        if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, flat.length - 1)); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
        else if (e.key === "Enter") {
            e.preventDefault();
            const target = flat[hi];
            if (target) handleAction(target);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 z-[120] bg-black/40 backdrop-blur-[2px] flex items-start justify-center p-4 pt-[12vh]" onClick={() => setOpen(false)} data-testid="power-menu">
            <div className="w-full max-w-xl bg-white border border-border shadow-2xl" onClick={(e) => e.stopPropagation()}>
                <div className="px-3 py-2.5 border-b border-border flex items-center gap-2">
                    <Search className="w-4 h-4 text-foreground/60" />
                    <input
                        ref={inputRef}
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={onKey}
                        placeholder="Search actions, candidates, jobs…"
                        className="flex-1 text-sm outline-none border-0 bg-transparent placeholder:text-foreground/40"
                        data-testid="power-menu-input"
                    />
                    <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/40 border border-border px-1.5 py-0.5">ESC</span>
                </div>
                <div className="max-h-[60vh] overflow-y-auto py-1">
                    {filteredActions.length > 0 && (
                        <Group label="Actions">
                            {filteredActions.map((a, idx) => (
                                <Row key={a.id} item={{ kind: "action", ...a }} active={hi === idx} onSelect={handleAction} onHover={() => setHi(idx)} testIdPrefix="power-action">
                                    <a.icon className="w-3.5 h-3.5 text-ikb shrink-0" />
                                    <span>{a.label}</span>
                                    {a.kbd && <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-foreground/50 border border-border px-1">{a.kbd}</span>}
                                </Row>
                            ))}
                        </Group>
                    )}
                    {cands.length > 0 && (
                        <Group label="Candidates">
                            {cands.map((c, i) => {
                                const idx = filteredActions.length + i;
                                return (
                                    <Row key={c.id} item={{ kind: "candidate", id: c.id, label: c.name }} active={hi === idx} onSelect={handleAction} onHover={() => setHi(idx)} testIdPrefix="power-cand">
                                        <UserPlus className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                        <div className="min-w-0">
                                            <div className="text-sm truncate">{c.name || c.email}</div>
                                            <div className="text-[10px] font-mono text-foreground/50 truncate">{c.current_title || c.email}</div>
                                        </div>
                                        <ChevronRight className="w-3 h-3 text-foreground/40 ml-auto" />
                                    </Row>
                                );
                            })}
                        </Group>
                    )}
                    {jobs.length > 0 && (
                        <Group label="Jobs">
                            {jobs.map((j, i) => {
                                const idx = filteredActions.length + cands.length + i;
                                return (
                                    <Row key={j.id} item={{ kind: "job", id: j.id, label: j.title }} active={hi === idx} onSelect={handleAction} onHover={() => setHi(idx)} testIdPrefix="power-job">
                                        <Briefcase className="w-3.5 h-3.5 text-ikb shrink-0" />
                                        <span className="truncate">{j.title}</span>
                                        {j.status && <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-foreground/50">{j.status}</span>}
                                    </Row>
                                );
                            })}
                        </Group>
                    )}
                    {flat.length === 0 && (
                        <div className="px-4 py-6 text-xs text-foreground/50 text-center">No results.</div>
                    )}
                </div>
                <div className="border-t border-border px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest text-foreground/40 flex items-center justify-between">
                    <span>↑ ↓ navigate · ⏎ select</span>
                    <span>⌘K to toggle</span>
                </div>
            </div>
        </div>
    );
}

function Group({ label, children }) {
    return (
        <div className="py-1">
            <div className="px-3 py-1 text-[9px] font-mono uppercase tracking-widest text-foreground/40">{label}</div>
            {children}
        </div>
    );
}

function Row({ item, active, onSelect, onHover, children, testIdPrefix }) {
    return (
        <button
            type="button"
            onMouseEnter={onHover}
            onClick={() => onSelect(item)}
            className={`w-full text-left px-3 py-2 text-sm inline-flex items-center gap-2 ${active ? "bg-secondary" : "hover:bg-secondary/50"}`}
            data-testid={`${testIdPrefix}-${item.id}`}
        >
            {children}
        </button>
    );
}
